import type { ReactNode } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import type { PageStateProps } from "@/components/layout/PageState";
import { RecordPageTemplate } from "@/components/templates/RecordPageTemplate";
import type { RecordAlert } from "@/components/templates/parts";
import { Badge } from "@/components/ui/badge";
import { getReportCategoryMetadata } from "@/lib/reports/report-categories";
import { RoutePending } from "@/lib/shared-ui/a11y/RoutePending";
import {
  reportRecordBreadcrumbs,
  reportRecordPath,
  reportRecordTabs,
  type ReportRecordSection,
} from "./reportRecordSections";

export interface ReportRecordNextProps {
  reportKey: string;
  section: ReportRecordSection;
  /** The URL query (the report's parameters), carried onto every tab link. */
  search: string;
  name: string;
  description?: string;
  /** The report's category key, shown as the hero badge and avatar icon. */
  category?: string;
  /** Period and generated time, inline under the name. */
  meta?: ReactNode;
  /** The report's first load: the hero shimmers and the tabs stay real (T2 loading). */
  loading?: boolean;
  /** Not found or no permission (RecordPageTemplate places it). */
  state?: Extract<PageStateProps, { kind: "error" | "forbidden" }>;
  /** Off for a report Forge doesn't have: there are no sections to open. */
  showTabs?: boolean;
  historyCount?: number;
  /** Secondary actions, then the one gold primary action (right-most). */
  actions?: ReactNode;
  alerts?: RecordAlert[];
  rail?: ReactNode;
  /** Rendered after the template: overlays such as the row drawer. */
  after?: ReactNode;
  /**
   * On Schedule and History: the Results content, chart included, mounted for print only. Cmd-P
   * then prints the report from any section, as it does from Results (and as the classic page
   * always did).
   */
  printResults?: ReactNode;
  /** The active section's content. */
  children: ReactNode;
}

/**
 * The print-only Results, out of sight on screen but laid out: never `hidden`, whose box measures
 * zero, so its chart (a responsive container) would print blank and the PDF export, which captures
 * that chart off Results, would have none. Clipped to no height and invisible here; `src/index.css`
 * restores it in print.
 */
const PRINT_ONLY_RESULTS_CLASS = "invisible h-0 overflow-hidden";

/**
 * `inert`, with `aria-hidden`, keeps the print-only copy out of focus, activation and the
 * accessibility tree. React 18 has no typed prop for it, so the attribute is set directly.
 */
const markInert = (el: HTMLDivElement | null) => {
  el?.setAttribute("inert", "");
};

/**
 * The report runner on T2 (`RecordPageTemplate`), under `ui_uplift` next. Presentational: the
 * page keeps its data hooks and passes the zones in.
 *
 * `report-runner-page` is the print scope `src/index.css` already gives the classic runner, so
 * Cmd-P still prints the report rather than the app chrome; `report-record-page` scopes the print
 * rules that are the record's alone (its cover's, over the global header rule). On Schedule and
 * History the section is `data-report-print="hide"` and `printResults` sits beside it as
 * `data-report-print="results"`, shown only in print.
 */
export function ReportRecordNext({
  reportKey,
  section,
  search,
  name,
  description,
  category,
  meta,
  loading = false,
  state,
  showTabs = true,
  historyCount,
  actions,
  alerts,
  rail,
  after,
  printResults,
  children,
}: ReportRecordNextProps) {
  const categoryMeta = category ? getReportCategoryMetadata(category) : null;
  const CategoryIcon = categoryMeta?.icon;
  return (
    // Same tree loading and loaded, so focus moved to the h1 on arrival survives the data.
    <RoutePending active={loading}>
      <AppLayout>
        <div className="report-runner-page report-record-page">
          <RecordPageTemplate
            module="Reports"
            breadcrumbs={reportRecordBreadcrumbs(name)}
            identity={{
              name,
              avatarFallback: CategoryIcon ? <CategoryIcon className="size-6" aria-hidden="true" /> : undefined,
              contactLine: description,
              badges: categoryMeta ? <Badge variant="outline">{categoryMeta.label}</Badge> : undefined,
              meta,
            }}
            actions={actions}
            alerts={alerts}
            tabs={showTabs ? reportRecordTabs(reportKey, search, { history: historyCount }) : undefined}
            activeTab={section}
            recordPath={reportRecordPath(reportKey)}
            rail={rail}
            railLabel={`About ${name}`}
            loading={loading}
            state={state}
          >
            {printResults && section !== "results" ? (
              <>
                <div data-report-print="hide">{children}</div>
                <div
                  ref={markInert}
                  aria-hidden="true"
                  data-report-print="results"
                  className={PRINT_ONLY_RESULTS_CLASS}
                >
                  {printResults}
                </div>
              </>
            ) : children}
          </RecordPageTemplate>
          {after}
        </div>
      </AppLayout>
    </RoutePending>
  );
}
